import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '../../utils/notificationUtils';

const SOAPDetailsModal = ({ isOpen, onClose, visitId, visitType }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n?.dir?.() === 'rtl';

  const [soapData, setSoapData] = useState({
    serial: '',
    dateTime: '',
    ownerComplaint: '',
    subjective: '',
    severity: '',
    objective: '',
    initialDiagnosis: '',
    assessment: '',
    plan: '',
    initialDiagnosis: '',
    assessment: '',
    plan: '',
    finalDiagnosis: '',
    history: []
  });

  const [vitalSigns, setVitalSigns] = useState({
    serial: '',
    temperature: '',
    heartRate: '',
    respiratoryRate: '',
    capillaryRefillTime: '',
    bodyWeight: '',
    hydrationStatus: '',
    mucousMembraneColor: '',
    mucousMembraneColor: '',
    bodyConditionScore: '',
    urine: '',
    stool: '',
    oralCavity: ''
  });

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vitalSignsError, setVitalSignsError] = useState(null);
  const [vitalSignsLoading, setVitalSignsLoading] = useState(false);

  // Load SOAP notes and vital signs when modal opens
  useEffect(() => {
    if (isOpen && visitId && visitType) {
      loadSOAPData();
      loadVitalSigns(); // Load vital signs independently
      loadServices();   // Load services if available
    } else if (isOpen && (!visitId || !visitType)) {
      setError(t('petProfile.medicalRecords.soapModal.missingVisitInfo'));
      setLoading(false);
    }
  }, [isOpen, visitId, visitType]);

  // Handle keyboard events and body scroll
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const loadSOAPData = async () => {
    if (!visitId || !visitType) return;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = visitType === 'inpatient'
        ? `${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${visitId}`
        : `${import.meta.env.VITE_API_URL}/api/v1/soap-notes/outpatient-visit/${visitId}`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.length > 0) {
          // Use the most recent SOAP record
          const latestSOAP = data.data[0];
          setSoapData({
            serial: latestSOAP.serial || '',
            dateTime: latestSOAP.date_time || '',
            ownerComplaint: latestSOAP.owner_complaint || '',
            subjective: latestSOAP.subjective || '',
            severity: latestSOAP.severity || '',
            objective: latestSOAP.objective || '',
            initialDiagnosis: latestSOAP.initial_diagnosis || '',
            assessment: latestSOAP.assessment || '',
            plan: latestSOAP.plan || '',
            initialDiagnosis: latestSOAP.initial_diagnosis || '',
            assessment: latestSOAP.assessment || '',
            plan: latestSOAP.plan || '',
            finalDiagnosis: latestSOAP.final_diagnosis || latestSOAP.final_diag || '',
            history: latestSOAP.diagnosis_history || []
          });
        } else {
          setError(t('petProfile.medicalRecords.soapModal.noData'));
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = errorData.message || t('petProfile.medicalRecords.soapModal.loadError');

        // Handle specific error codes
        if (response.status === 401 || response.status === 403) {
          errorMessage = t('petProfile.medicalRecords.soapModal.unauthorized');
        } else if (response.status === 404) {
          errorMessage = t('petProfile.medicalRecords.soapModal.notFound');
        }

        setError(errorMessage);
        // Don't show toast for authorization errors as they're already shown in the modal
        if (response.status !== 401 && response.status !== 403) {
          showErrorToast(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error loading SOAP data:', error);
      let errorMessage = t('petProfile.medicalRecords.soapModal.loadError');

      // Handle network errors
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        errorMessage = t('petProfile.medicalRecords.soapModal.networkError');
      }

      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadVitalSigns = async () => {
    if (!visitId || !visitType) return;

    setVitalSignsLoading(true);
    setVitalSignsError(null);

    try {
      const token = localStorage.getItem('access_token');
      const endpoint = visitType === 'inpatient'
        ? `${import.meta.env.VITE_API_URL}/api/v1/vital-signs/inpatient-visit/${visitId}`
        : `${import.meta.env.VITE_API_URL}/api/v1/vital-signs/outpatient-visit/${visitId}`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data && result.data.length > 0) {
          const latestVitalSigns = result.data[0];
          setVitalSigns({
            serial: latestVitalSigns.serial || '',
            temperature: latestVitalSigns.temperature || '',
            heartRate: latestVitalSigns.heart_rate || latestVitalSigns.heartRate || '',
            respiratoryRate: latestVitalSigns.respiratory_rate || latestVitalSigns.respiratoryRate || '',
            capillaryRefillTime: latestVitalSigns.capillary_refill_time || latestVitalSigns.capillaryRefillTime || '',
            bodyWeight: latestVitalSigns.body_weight || latestVitalSigns.bodyWeight || '',
            hydrationStatus: latestVitalSigns.hydration_status || latestVitalSigns.hydrationStatus || '',
            mucousMembraneColor: latestVitalSigns.mucous_membrane_color || latestVitalSigns.mucousMembraneColor || '',
            bodyConditionScore: latestVitalSigns.body_condition_score || latestVitalSigns.bodyConditionScore || '',
            urine: latestVitalSigns.urine || '',
            stool: latestVitalSigns.stool || '',
            oralCavity: latestVitalSigns.oral_cavity || latestVitalSigns.oralCavity || '',
            lymphNodes: latestVitalSigns.lymph_nodes || latestVitalSigns.lymphNodes || ''
          });

          // Sync severity (Pain Score) and objective (Physical Exam Findings) if returned in vital signs
          if (latestVitalSigns.severity) {
            setSoapData(prev => ({ ...prev, severity: latestVitalSigns.severity }));
          }
          if (latestVitalSigns.objective || latestVitalSigns.physical_exam_findings) {
            setSoapData(prev => ({ ...prev, objective: latestVitalSigns.objective || latestVitalSigns.physical_exam_findings || '' }));
          }
        } else {
          // No vital signs data - this is fine, they're optional
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401 || response.status === 403) {
          setVitalSignsError(t('petProfile.medicalRecords.soapModal.unauthorized'));
        } else {
          setVitalSignsError(null); // Don't show error for other status codes
          console.error('Vital signs API error:', response.status, errorData);
        }
      }
    } catch (error) {
      console.error('Error loading vital signs:', error);
      setVitalSignsError(null); // Don't show error for network issues
    } finally {
      setVitalSignsLoading(false);
    }
  };

  const loadServices = async () => {
    if (!visitId || !visitType) return;

    setServicesLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = visitType === 'inpatient'
        ? `${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${visitId}/details`
        : `${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${visitId}/details`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data) {
          const normalized = (result.data || []).map(vd => ({
            id: vd.id,
            service_name_en: vd.clinic_service?.service_name_en || vd.name || '',
            quantity: vd.qty || vd.quantity || 1,
            is_fulfilled: !!vd.is_fulfilled,
            notes: vd.notes || ''
          }));
          setServices(normalized);
        }
      }
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setServicesLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: 600,
              color: '#111827',
              fontFamily: '"Red Hat Display", sans-serif',
            }}
          >
            {t('petProfile.medicalRecords.soapModal.title')}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6B7280',
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#F3F4F6';
              e.target.style.color = '#111827';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6B7280';
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '24px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #F3F4F6',
                  borderTop: '4px solid #3B82F6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <p style={{ marginTop: '16px', color: '#6B7280' }}>
                {t('petProfile.medicalRecords.soapModal.loading')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Show SOAP error if exists, but still show vital signs */}
              {error && (
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '8px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#DC2626"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <circle cx="12" cy="16" r="0.8" fill="currentColor" stroke="none" />
                    </svg>
                    <p
                      style={{
                        margin: 0,
                        color: '#DC2626',
                        fontSize: '14px',
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      {error}
                    </p>
                  </div>
                </div>
              )}

              {/* Show SOAP data if available */}
              {!error && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* Owner's Complaint */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '8px',
                        fontFamily: '"Red Hat Display", sans-serif',
                      }}
                    >
                      {t('petProfile.medicalRecords.soapModal.ownersComplaint').replace("Owner's Complaint", "Guardian's Complaint & Pet's History")}
                    </label>
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        minHeight: '60px',
                        color: '#111827',
                        fontSize: '14px',
                        fontFamily: '"Red Hat Display", sans-serif',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {soapData.ownerComplaint || '-'}
                    </div>
                  </div>



                  {/* Physical Exam (Objective) */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '8px',
                        fontFamily: '"Red Hat Display", sans-serif',
                      }}
                    >
                      {t('petProfile.medicalRecords.soapModal.physicalExam')}
                    </label>
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        minHeight: '60px',
                        color: '#111827',
                        fontSize: '14px',
                        fontFamily: '"Red Hat Display", sans-serif',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {soapData.objective || '-'}
                    </div>
                  </div>

                  {/* Initial Diagnosis */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '8px',
                        fontFamily: '"Red Hat Display", sans-serif',
                      }}
                    >
                      {soapData.history && soapData.history.length > 0 ? 'Differential Diagnosis' : t('petProfile.medicalRecords.soapModal.initialDiagnosis')}
                    </label>
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        minHeight: '60px',
                        color: '#111827',
                        fontSize: '14px',
                        fontFamily: '"Red Hat Display", sans-serif',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {soapData.initialDiagnosis || '-'}
                    </div>
                  </div>

                  {/* Differential Diagnosis History */}
                  {soapData.history && soapData.history.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#6B7280',
                        marginBottom: '8px',
                        fontFamily: '"Red Hat Display", sans-serif',
                      }}>
                        Previous Differential Diagnoses
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {soapData.history.map((entry, index) => (
                          <div key={index} style={{
                            padding: '12px',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '8px',
                            border: '1px solid #E5E7EB'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: '#4B5563', fontFamily: '"Red Hat Display", sans-serif' }}>
                                {index === soapData.history.length - 1 ? 'Initial Diagnosis' : `Differential Diagnosis ${soapData.history.length - 1 - index}`} • {entry.by}
                              </span>
                              <span style={{ fontSize: '12px', color: '#9CA3AF', fontFamily: '"Red Hat Display", sans-serif' }}>
                                {new Date(entry.date).toLocaleString()}
                              </span>
                            </div>
                            <div style={{
                              fontSize: '14px',
                              color: '#374151',
                              whiteSpace: 'pre-wrap',
                              fontFamily: '"Red Hat Display", sans-serif'
                            }}>
                              {entry.diagnosis}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Final Diagnosis */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '8px',
                        fontFamily: '"Red Hat Display", sans-serif',
                      }}
                    >
                      {t('petProfile.medicalRecords.soapModal.finalDiagnosis')}
                    </label>
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        minHeight: '60px',
                        color: '#111827',
                        fontSize: '14px',
                        fontFamily: '"Red Hat Display", sans-serif',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {soapData.finalDiagnosis || '-'}
                    </div>
                  </div>



                  {/* Services & Medications Checklist */}
                  {services && services.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#6B7280',
                        marginBottom: '8px',
                        fontFamily: '"Red Hat Display", sans-serif',
                      }}>
                        Services & Medications Checklist
                      </label>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        background: '#F8FAFC',
                        padding: '12px',
                        borderRadius: '12px',
                        border: '1px solid #E2E8F0'
                      }}>
                        {services.map((service, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px',
                            backgroundColor: '#FFFFFF',
                            borderRadius: '8px',
                            border: '1px solid #F1F5F9'
                          }}>
                            <div style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: service.is_fulfilled ? '#DCFCE7' : '#F1F5F9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: service.is_fulfilled ? '#166534' : '#94A3B8',
                              fontSize: '12px'
                            }}>
                              <i className="fas fa-check"></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1E293B', fontFamily: '"Red Hat Display", sans-serif' }}>
                                  {service.service_name_en}
                                </span>
                                {service.quantity && (
                                  <span style={{ fontSize: '11px', color: '#64748B', fontFamily: '"Red Hat Display", sans-serif' }}>
                                    Qty: {service.quantity}
                                  </span>
                                )}
                              </div>
                              {service.notes && (
                                <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px', fontFamily: '"Red Hat Display", sans-serif' }}>
                                  {service.notes}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Vital Signs - Always show, even if SOAP notes failed */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827',
                    marginBottom: '16px',
                    fontFamily: '"Red Hat Display", sans-serif',
                  }}
                >
                  {t('petProfile.medicalRecords.soapModal.vitalSigns')}
                </label>
                {vitalSignsLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280' }}>
                    {t('petProfile.medicalRecords.soapModal.loading')}
                  </div>
                ) : vitalSignsError ? (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      borderRadius: '8px',
                      color: '#DC2626',
                      fontSize: '14px',
                    }}
                  >
                    {vitalSignsError}
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '24px',
                    }}
                  >
                    {[
                      { key: 'temperature', label: 'Temperature', value: vitalSigns.temperature || '-' },
                      { key: 'heartRate', label: 'Heart Rate', value: vitalSigns.heartRate || '-' },
                      { key: 'respiratoryRate', label: 'Respiratory Rate', value: vitalSigns.respiratoryRate || '-' },
                      { key: 'capillaryRefillTime', label: 'CRT', value: vitalSigns.capillaryRefillTime || '-' },
                      { key: 'bodyWeight', label: 'Weight', value: vitalSigns.bodyWeight || '-' },
                      { key: 'hydrationStatus', label: 'Hydration', value: vitalSigns.hydrationStatus || '-' },
                      { key: 'mucousMembraneColor', label: 'Mucous Membrane Color', value: vitalSigns.mucousMembraneColor || '-' },
                      { key: 'bodyConditionScore', label: 'BCS', value: vitalSigns.bodyConditionScore || '-' },
                      { key: 'urine', label: 'Urine', value: vitalSigns.urine || '-' },
                      { key: 'stool', label: 'Stool', value: vitalSigns.stool || '-' },
                      { key: 'oralCavity', label: 'Oral Cavity', value: vitalSigns.oralCavity || '-' },
                      { key: 'severity', label: 'Pain Score', value: soapData.severity || '-' },
                    ].map((item) => (
                      <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <label
                          style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#374151',
                            margin: 0,
                            minWidth: '170px',
                            fontFamily: '"Red Hat Display", sans-serif',
                          }}
                        >
                          {item.label}
                        </label>
                        <div
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#F9FAFB',
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB',
                            color: '#111827',
                            fontSize: '14px',
                            fontFamily: '"Red Hat Display", sans-serif',
                            minWidth: '200px'
                          }}
                        >
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: '#3B82F6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontFamily: '"Red Hat Display", sans-serif',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#2563EB';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#3B82F6';
            }}
          >
            {t('petProfile.medicalRecords.soapModal.close')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SOAPDetailsModal;

